import { useEffect, useRef } from 'react';
import { api } from './api';

// The lock-screen safety net for a running timer.
//
// A PWA cannot draw a live countdown on the iOS Lock Screen — that needs
// ActivityKit, which is native-only, and there is no web equivalent. And once
// the screen is dark iOS suspends our JavaScript within seconds, so the in-app
// cue can't fire either. What iOS *does* deliver to a locked phone is a push.
//
// So while a timer runs we ask the server to hold a one-shot notification for
// the moment it ends, and cancel it the instant the timer stops — whether that's
// because you skipped it, paused it, or because it ran out while the app was
// still awake and sounded the cue itself. The push is a fallback, not the
// primary cue; you should only ever hear one of the two.
//
// endsAt is the wall-clock ms at which the timer expires, or null when it isn't
// running. Failures are swallowed: no network or no push subscription just means
// no safety net, and the on-screen timer is unaffected.
export function useBackupPush(endsAt: number | null, title: string, label: string): void {
  const scheduledIDRef = useRef(0);

  useEffect(() => {
    if (endsAt === null) return;
    const seconds = Math.round((endsAt - Date.now()) / 1000);
    if (seconds <= 0) return;

    let cancelled = false;
    void api
      .startRest(seconds, label, title)
      .then((r) => {
        // The timer may have already stopped while this request was in flight.
        if (cancelled) {
          void api.cancelRest(r.id).catch(() => undefined);
          return;
        }
        scheduledIDRef.current = r.id;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      const id = scheduledIDRef.current;
      scheduledIDRef.current = 0;
      if (id > 0) void api.cancelRest(id).catch(() => undefined);
    };
  }, [endsAt, title, label]);
}
